<script>
  /**
   * App.svelte - Main entry point for Preparo
   *
   * Complete integration example with the Milkdown editor
   */

  import { onMount } from 'svelte';

  // Auth gate component (handles SSO authentication)
  import AuthGate from './interface/components/auth/AuthGate.svelte';

  // Layout components
  import ProjectsPanel from './interface/components/layout/ProjectsPanel.svelte';
  import DocsPanel from './interface/components/layout/DocsPanel.svelte';
  import NotesPanel from './interface/components/layout/NotesPanel.svelte';

  // Stores
  import {
    appActions,
    isLoading,
    initializeDatabase
  } from './interface/stores/appStore';

  // i18n
  import { i18n, currentLanguage, currentLanguageInfo } from './interface/stores/i18nStore';

  // Config
  import { ui } from './interface/config';

  // Auth service (for portal URL)
  import { getPortalUrl } from './interface/services/supabaseClient';
  import { isAuthLoading } from './interface/stores/authStore';

  // Sync indicator & conflict resolution
  import SyncIndicator from './interface/components/layout/SyncIndicator.svelte';
  import ConflictResolutionModal from './interface/components/layout/ConflictResolutionModal.svelte';

  // Git sync service
  import { init as initGitSync, hydrateFromGit, setupOfflineDetection, markUnsyncedPresetsDirty, lastHydrationResult } from './interface/services/gitSyncService';

  // Styles (variables.css must be imported first)
  import './interface/styles/variables.css';
  import './interface/styles/milkdown-custom.css';

  // Layout configuration
  let projectsPanelWidth = ui.projectsPanelWidth;
  let docsPanelWidth = ui.docsPanelWidth;
  let resizingProjectsPanel = false;
  let resizingDocsPanel = false;
  let sidePanelsVisible = true;
  let dbInitialized = false;
  let dbError = null;
  let hydrating = false;

  // Theme and menu state
  let menuOpen = false;
  let theme = 'system'; // 'light', 'dark', or 'system'

  // Initialize theme from localStorage
  function initTheme() {
    const savedTheme = localStorage.getItem('preparo-theme') || 'system';
    theme = savedTheme;
    applyTheme(savedTheme);
  }

  // Apply theme to document
  function applyTheme(newTheme) {
    const root = document.documentElement;
    if (newTheme === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else if (newTheme === 'light') {
      root.setAttribute('data-theme', 'light');
    } else {
      // System: remove attribute and let CSS media query handle it
      root.removeAttribute('data-theme');
    }
  }

  // Toggle between light and dark (cycles: light -> dark -> system)
  function cycleTheme() {
    if (theme === 'light') {
      theme = 'dark';
    } else if (theme === 'dark') {
      theme = 'system';
    } else {
      theme = 'light';
    }
    localStorage.setItem('preparo-theme', theme);
    applyTheme(theme);
  }

  // Get current theme label for display (using i18n)
  function getThemeLabel(t) {
    if (t === 'light') return $i18n('app.theme.light');
    if (t === 'dark') return $i18n('app.theme.dark');
    return $i18n('app.theme.system');
  }

  // Get theme icon
  function getThemeIcon(t) {
    if (t === 'light') return '☀️';
    if (t === 'dark') return '🌙';
    return '💻';
  }

  // Toggle language
  function toggleLanguage() {
    currentLanguage.toggle();
  }

  // Close menu when clicking outside
  function handleClickOutside(event) {
    if (menuOpen && !event.target.closest('.hamburger-menu')) {
      menuOpen = false;
    }
  }

  /** Wait for AuthGate to finish initializing auth (SSO token handling) */
  function waitForAuth() {
    return new Promise((resolve) => {
      const unsub = isAuthLoading.subscribe((loading) => {
        if (!loading) {
          // Use queueMicrotask to unsubscribe outside the notification cycle
          queueMicrotask(() => unsub());
          resolve();
        }
      });
    });
  }

  // Initial loading (called after AuthGate confirms authentication)
  async function initializeApp() {
    // Initialize theme
    initTheme();

    try {
      // Initialize IndexedDB and migrate localStorage data
      await initializeDatabase();

      // Wait for auth to be ready before initializing git sync
      // (AuthGate handles SSO token exchange in its own onMount)
      await waitForAuth();

      // Initialize git sync (checks provisioning, starts offline detection)
      const syncReady = await initGitSync();
      setupOfflineDetection();

      // Hydrate from Git only if sync is ready (user provisioned)
      // Projects: only stub records are created (lazy hydration on click)
      // Presets: eagerly hydrated (useful immediately)
      if (syncReady) {
        hydrating = true;
        try {
          const hydrationResult = await hydrateFromGit();
          if (hydrationResult.presetsHydrated > 0 || hydrationResult.projectsHydrated > 0) {
            console.info('Hydration complete:', hydrationResult);
          }
        } catch (err) {
          console.warn('Background hydration failed:', err);
        } finally {
          hydrating = false;
        }

        // Mark any local presets that were never synced as dirty
        markUnsyncedPresetsDirty().catch(err =>
          console.warn('Failed to mark unsynced presets:', err)
        );
      }

      // Load projects and presets from IndexedDB (stubs + hydrated presets)
      await appActions.loadProjects();
      await appActions.loadPresets();

      // Show the main layout only after everything is ready
      dbInitialized = true;
    } catch (error) {
      console.error('Failed to initialize app:', error);
      dbError = error.message;
    }
  }

  onMount(() => {
    initializeApp();
  });

  // Refresh project list when hydration completes after reconnection.
  // During initializeApp we already await hydration before loadProjects,
  // but reconcileAfterReconnection runs later and also calls hydrateFromGit.
  let hydrationHandledOnce = false;
  $: if ($lastHydrationResult) {
    if (hydrationHandledOnce) {
      if ($lastHydrationResult.projectsHydrated > 0) appActions.loadProjects();
      if ($lastHydrationResult.presetsHydrated > 0) appActions.loadPresets();
    }
    hydrationHandledOnce = true;
  }

  // Panel resize handling
  function startResizeProjects(e) {
    resizingProjectsPanel = true;
    e.preventDefault();
  }

  function startResizeDocs(e) {
    resizingDocsPanel = true;
    e.preventDefault();
  }

  function handleMouseMove(e) {
    if (resizingProjectsPanel) {
      projectsPanelWidth = Math.max(ui.projectsPanelMinWidth, Math.min(ui.projectsPanelMaxWidth, e.clientX));
    } else if (resizingDocsPanel) {
      docsPanelWidth = Math.max(ui.docsPanelMinWidth, Math.min(ui.docsPanelMaxWidth, e.clientX - projectsPanelWidth));
    }
  }

  function handleMouseUp() {
    resizingProjectsPanel = false;
    resizingDocsPanel = false;
  }
</script>

<svelte:window
  on:mousemove={handleMouseMove}
  on:mouseup={handleMouseUp}
  on:click={handleClickOutside}
/>

<AuthGate>
{#if dbError}
  <!-- Database initialization error -->
  <div class="app-error">
    <div class="error-content">
      <h2>⚠️ {$i18n('app.databaseError')}</h2>
      <p>{$i18n('app.databaseErrorMessage')}</p>
      <code>{dbError}</code>
      <button on:click={() => window.location.reload()}>{$i18n('app.reload')}</button>
    </div>
  </div>
{:else if !dbInitialized}
  <!-- Loading state — stays visible until projects are loaded -->
  <div class="app-loading">
    <div class="loading-content">
      <div class="spinner"></div>
      <p>{hydrating ? $i18n('app.syncing') : $i18n('app.initializing')}</p>
    </div>
  </div>
{:else}
<div class="app" class:resizing={resizingProjectsPanel || resizingDocsPanel}>
  <!-- Toolbar / Header -->
  <header class="app-header">
    <div class="header-left">
      <!-- Hamburger menu -->
      <div class="hamburger-menu">
        <button
          class="hamburger-btn"
          on:click={() => menuOpen = !menuOpen}
          title="Menu"
          aria-label="Menu"
          aria-expanded={menuOpen}
        >
          <span class="hamburger-icon">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>

        {#if menuOpen}
          <div class="dropdown-menu">
            <button class="menu-item" on:click={cycleTheme}>
              <span class="menu-icon">{getThemeIcon(theme)}</span>
              <span class="menu-label">{$i18n('app.theme')}: {getThemeLabel(theme)}</span>
            </button>
            <button class="menu-item" on:click={toggleLanguage}>
              <span class="menu-icon">{$currentLanguageInfo.flag}</span>
              <span class="menu-label">{$i18n('app.language')}: {$currentLanguageInfo.label}</span>
            </button>
          </div>
        {/if}
      </div>

      <button
        class="toggle-sidebar-btn"
        on:click={() => sidePanelsVisible = !sidePanelsVisible}
        title={sidePanelsVisible ? $i18n('app.hidePanel') : $i18n('app.showPanels')}
      >
        {sidePanelsVisible ? '◀' : '▶'}
      </button>
    </div>

    <div class="header-center">
      <h1 class="app-title">preparo</h1>
    </div>

    <div class="header-right">
      {#if hydrating}
        <span class="loading-indicator">{$i18n('app.syncing')}</span>
      {:else if $isLoading}
        <span class="loading-indicator">{$i18n('app.loading')}</span>
      {/if}
      <SyncIndicator />
      <ConflictResolutionModal />
      <a href={getPortalUrl()} class="portal-link" title="Retour au portail Spectral Cloud">
        <img src="/logo.png" alt="Spectral Cloud" class="header-logo" />
      </a>
    </div>
  </header>

  <!-- Main 3-panel layout -->
  <div class="app-layout">
    {#if sidePanelsVisible}
      <!-- Projects panel (left) -->
      <div
        class="panel projects-panel"
        style="width: {projectsPanelWidth}px;"
      >
        <ProjectsPanel />
      </div>

      <!-- Resizable separator -->
      <div
        class="panel-resizer"
        on:mousedown={startResizeProjects}
      ></div>

      <!-- Documents panel (center) -->
      <div
        class="panel docs-panel"
        style="width: {docsPanelWidth}px;"
      >
        <DocsPanel />
      </div>

      <!-- Resizable separator -->
      <div
        class="panel-resizer"
        on:mousedown={startResizeDocs}
      ></div>
    {/if}

    <!-- Notes / Details panel (right) -->
    <div class="panel notes-panel">
      <NotesPanel />
    </div>
  </div>
</div>
{/if}
</AuthGate>

<style>
  :global(*) {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  :global(body) {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    overflow: hidden;
  }

  .app {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: var(--app-background, #f9fafb);
  }

  .app.resizing {
    cursor: col-resize;
    user-select: none;
  }

  /* Header */
  .app-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: white;
    border-bottom: 1px solid #e5e7eb;
    min-height: 40px;
    flex-shrink: 0;
    position: relative;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .toggle-sidebar-btn {
    background: transparent;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 6px 12px;
    cursor: pointer;
    font-size: 12px;
    color: #6b7280;
    transition: all 0.2s;
  }

  .toggle-sidebar-btn:hover {
    background: #f9fafb;
    border-color: #3b82f6;
    color: #3b82f6;
  }

  /* Hamburger menu */
  .hamburger-menu {
    position: relative;
  }

  .hamburger-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: transparent;
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .hamburger-btn:hover {
    background: var(--hover-background, #f9fafb);
    border-color: var(--accent-color, #3b82f6);
  }

  .hamburger-icon {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 14px;
    height: 10px;
  }

  .hamburger-icon span {
    display: block;
    width: 100%;
    height: 2px;
    background: var(--text-muted, #6b7280);
    border-radius: 1px;
    transition: background 0.2s;
  }

  .hamburger-btn:hover .hamburger-icon span {
    background: var(--accent-color, #3b82f6);
  }

  .dropdown-menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    min-width: 180px;
    background: var(--panel-background, #ffffff);
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: 6px;
    box-shadow: var(--shadow-md, 0 4px 6px rgba(0, 0, 0, 0.1));
    z-index: var(--z-index-dropdown, 100);
    overflow: hidden;
  }

  .menu-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    padding: 0.625rem 0.875rem;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 0.875rem;
    color: var(--text-primary, #1f2937);
    text-align: left;
    transition: background 0.15s;
  }

  .menu-item:hover {
    background: var(--hover-background, #f9fafb);
  }

  .menu-icon {
    font-size: 1rem;
    width: 1.25rem;
    text-align: center;
  }

  .menu-label {
    flex: 1;
  }

  .app-title {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .header-center {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }

  .portal-link {
    display: flex;
    align-items: center;
    opacity: 0.7;
    transition: opacity 0.2s;
  }

  .portal-link:hover {
    opacity: 1;
  }

  .header-logo {
    height: 24px;
    width: auto;
  }

  .loading-indicator {
    font-size: 0.85rem;
    color: var(--text-muted, #6b7280);
    animation: pulse-opacity 1.5s ease-in-out infinite;
  }

  @keyframes pulse-opacity {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }

  /* Layout */
  .app-layout {
    display: flex;
    flex: 1;
    overflow: hidden;
  }

  .panel {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: white;
  }

  .projects-panel {
    border-right: 1px solid #e5e7eb;
    flex-shrink: 0;
  }

  .docs-panel {
    border-right: 1px solid #e5e7eb;
    flex-shrink: 0;
  }

  .notes-panel {
    flex: 1;
    min-width: 400px;
  }

  /* Resizer */
  .panel-resizer {
    width: 4px;
    background: transparent;
    cursor: col-resize;
    flex-shrink: 0;
    transition: background 0.2s;
  }

  .panel-resizer:hover,
  .app.resizing .panel-resizer {
    background: #3b82f6;
  }

  /* App loading and error states */
  .app-loading,
  .app-error {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
    background: var(--app-background, #f9fafb);
  }

  .loading-content,
  .error-content {
    text-align: center;
    color: var(--text-primary, #1f2937);
  }

  .loading-content .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid var(--border-color, #e5e7eb);
    border-top-color: var(--accent-color, #3b82f6);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .error-content h2 {
    margin-bottom: 0.5rem;
    color: #ef4444;
  }

  .error-content code {
    display: block;
    margin: 1rem 0;
    padding: 0.5rem 1rem;
    background: var(--panel-background, #ffffff);
    border-radius: 4px;
    font-size: 0.875rem;
    color: #ef4444;
  }

  .error-content button {
    padding: 0.5rem 1rem;
    background: var(--accent-color, #3b82f6);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
  }

  .error-content button:hover {
    opacity: 0.9;
  }

  /* NOTE: Les variables CSS sont définies globalement dans variables.css */

  /* Dark mode specific overrides */
  @media (prefers-color-scheme: dark) {
    .app {
      background: var(--app-background);
    }

    .app-header,
    .panel {
      background: var(--panel-background);
      border-color: var(--border-color);
    }

    .app-title {
      color: var(--text-primary);
    }

    .toggle-sidebar-btn {
      border-color: var(--border-color);
      color: var(--text-muted);
    }

    .toggle-sidebar-btn:hover {
      background: var(--panel-background);
      border-color: var(--accent-color);
      color: var(--accent-color);
    }
  }

  /* Responsive */
  @media (max-width: 1024px) {
    .projects-panel {
      width: 200px !important;
    }

    .docs-panel {
      width: 300px !important;
    }
  }

  @media (max-width: 768px) {
    .app-layout {
      flex-direction: column;
    }

    .panel {
      width: 100% !important;
      height: auto;
    }

    .panel-resizer {
      display: none;
    }
  }
</style>
